import React, { memo, useEffect, useState, useCallback } from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import {
  Box,
  List,
  ListItem,
  Stack,
  Input,
  InputGroup,
  InputLeftElement,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  HStack,
  Button,
  Icon,
  IconButton,
  Text,
} from "@chakra-ui/react";
import { BiMenu, BiPlus, BiEdit, BiSearch, BiTrash } from "react-icons/bi";
import SnippetForm from "./SnippetForm";
import { trackBasicEvent } from "../../../_utils/eventsTracker";
import Preloader from "../Preloader/Preloader";
import PreloaderWrapper from "../Preloader/PreloaderWrapper";
import { translate as trans } from "../Trans/Trans";
import {
  getSnippets,
  addSnippet,
  editSnippet,
  removeSnippet,
  markSnippetAsUsed,
} from "../../../_actions/plannerActions";

const snippetStyles = {
  whiteSpace: "normal",
  display: "block",
  height: "auto",
  textAlign: "left",
  padding: 5,
  fontWeight: "normal",
  flex: 1,
};

const listWrapperStyles = {
  overflow: "auto",
  maxHeight: "250px",
};

const snippetTextStyles = {
  display: "block",
  marginTop: "10px",
};

const SnippetsPicker = ({ onSelect, snippetData }) => {
  const [searchValue, setSearchValue] = useState("");
  const [loading, setLoading] = useState(true);
  const [snippets, setSnippets] = useState([]);
  const [filteredSnippets, setFilteredSnippets] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [editedSnippet, setEditedSnippet] = useState(snippetData);
  const [formOpen, setFormOpen] = useState(!!snippetData);
  const dispatch = useDispatch();

  const handleSnippetSearch = useCallback(
    (event) => {
      const value = event.target.value || "";

      setSearchValue(value);
    },
    [setSearchValue]
  );

  const handleAddNewSnippet = useCallback(
    (event) => {
      setEditedSnippet(null);
      setFormOpen(true);
      event.stopPropagation();
    },
    [setFormOpen, setEditedSnippet]
  );

  const handleCloseForm = useCallback(
    (event) => {
      setFormOpen(false);
      setEditedSnippet(null);
      event.stopPropagation();
    },
    [setFormOpen, setEditedSnippet]
  );

  const loadSnippets = useCallback(async () => {
    const response = await dispatch(getSnippets());

    setSnippets(response?.data?.items || []);
    setLoading(false);
  }, [setSnippets, setLoading, dispatch]);

  const handleSaveSnippet = useCallback(
    async (updatedSnippet) => {
      setLoading(true);

      if (editedSnippet?.id) {
        await dispatch(
          editSnippet({
            text: updatedSnippet.text,
            title: updatedSnippet.title,
            id: editedSnippet.id,
          })
        );

        trackBasicEvent("snippets", "click", "updated");
      } else {
        await dispatch(addSnippet(updatedSnippet));

        trackBasicEvent("snippets", "click", "added");
      }

      setFormOpen(false);

      await loadSnippets();
      setLoading(false);
    },
    [setFormOpen, editedSnippet, setLoading, loadSnippets, dispatch]
  );

  const handleEditSnippet = useCallback(
    (snippet, event) => {
      setEditedSnippet(snippet);
      setFormOpen(true);
      event.stopPropagation();
    },
    [setFormOpen, setEditedSnippet]
  );

  const handleRemoveSnippet = useCallback(
    async (snippet) => {
      setLoading(true);
      const response = await dispatch(removeSnippet(snippet.id));

      if (response?.data?.status === 1) {
        await loadSnippets();
      }

      setLoading(false);
      trackBasicEvent("snippets", "click", "removed");
    },
    [setLoading, loadSnippets, dispatch]
  );

  const handleUseSnippet = useCallback(
    (snippet) => {
      dispatch(markSnippetAsUsed(snippet.id));
      onSelect(snippet);
    },
    [onSelect, dispatch]
  );

  useEffect(() => {
    if (!searchValue) {
      setFilteredSnippets(snippets);

      return;
    }

    const filtered = [];

    snippets.forEach((snippet) => {
      if (snippet.text.toLowerCase().includes(searchValue.toLowerCase())) {
        filtered.push({
          ...snippet,
          textShown: searchValue.length > 1, // avoid showing all snippets texts containing single letter
        });

        return;
      }

      if (
        snippet.title &&
        snippet.title.toLowerCase().includes(searchValue.toLowerCase())
      ) {
        filtered.push({
          ...snippet,
        });
      }
    });

    setFilteredSnippets(filtered);
  }, [searchValue, snippets]);

  useEffect(() => {
    loadSnippets();
    trackBasicEvent("snippets", "click", "open");
  }, []);

  return (
    <Box p={2} sx={{ position: "relative" }}>
      {loading && (
        <PreloaderWrapper>
          <Preloader size="md" />
        </PreloaderWrapper>
      )}
      {formOpen ? (
        <SnippetForm
          onSave={handleSaveSnippet}
          onCancel={handleCloseForm}
          editText={editedSnippet?.text}
          editTitle={editedSnippet?.title}
        />
      ) : (
        <Stack spacing={2}>
          {snippets?.length && (
            <HStack spacing={3}>
              <InputGroup size="xs">
                <InputLeftElement pointerEvents="none">
                  <Icon as={BiSearch} />
                </InputLeftElement>
                <Input
                  value={searchValue}
                  onChange={handleSnippetSearch}
                  type="text"
                  placeholder={trans("snippets.search")}
                />
              </InputGroup>
              <Menu isLazy autoSelect={false}>
                <MenuButton
                  as={IconButton}
                  aria-label="Options"
                  icon={<Icon as={BiMenu} />}
                  variant="outline"
                  size="xs"
                />
                <MenuList fontSize="sm">
                  <MenuItem
                    onClick={handleAddNewSnippet}
                    icon={<Icon as={BiPlus} />}
                  >
                    {trans("snippets.add")}
                  </MenuItem>
                  {editMode ? (
                    <MenuItem
                      onClick={() => setEditMode(false)}
                      icon={<Icon as={BiEdit} />}
                    >
                      {trans("snippets.edit_close")}
                    </MenuItem>
                  ) : (
                    <MenuItem
                      onClick={() => setEditMode(true)}
                      icon={<Icon as={BiEdit} />}
                    >
                      {trans("snippets.edit")}
                    </MenuItem>
                  )}
                </MenuList>
              </Menu>
            </HStack>
          )}
          {!loading && snippets.length === 0 && (
            <Text fontSize="xs">{trans("snippets.no_snippets")}</Text>
          )}
          <Box style={listWrapperStyles}>
            <List spacing={1}>
              {filteredSnippets.map((snippet) => {
                const handleSelect = () => handleUseSnippet(snippet);
                const handleDelete = () => handleRemoveSnippet(snippet);
                const handleEdit = (event) => handleEditSnippet(snippet, event);
                return (
                  <ListItem key={snippet.id}>
                    <HStack spacing={2} align="stretch">
                      <Button
                        style={snippetStyles}
                        variant="outline"
                        size="xs"
                        onClick={handleSelect}
                        title={snippet.title ? snippet.text : null}
                      >
                        {snippet.title ? snippet.title : snippet.text}
                        {snippet.textShown && (
                          <span style={snippetTextStyles}>{snippet.text}</span>
                        )}
                      </Button>
                      {editMode && (
                        <>
                          <IconButton
                            aria-label="Edit"
                            title="Edit"
                            size="xs"
                            icon={<Icon as={BiEdit} />}
                            onClick={handleEdit}
                          />
                          <IconButton
                            variant="danger"
                            aria-label="Delete"
                            title="Delete"
                            size="xs"
                            icon={<Icon as={BiTrash} />}
                            onClick={handleDelete}
                          />
                        </>
                      )}
                    </HStack>
                  </ListItem>
                );
              })}
            </List>
            <Box mt={2}>
              <Button
                onClick={handleAddNewSnippet}
                size="xs"
                variant="solid"
                leftIcon={<Icon as={BiPlus} />}
              >
                {trans("snippets.add")}
              </Button>
            </Box>
          </Box>
        </Stack>
      )}
    </Box>
  );
};

SnippetsPicker.propTypes = {
  onSelect: PropTypes.func.isRequired,
  snippetData: PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string,
    text: PropTypes.string,
  }),
};

SnippetsPicker.defaultProps = {
  snippetData: undefined,
};

export default memo(SnippetsPicker);
